export type ThemePreference = 'auto' | 'light' | 'dark'

const STORAGE_KEY = 'setting:theme'
const LEGACY_KEY = 'setting:light-theme'

const lightQuery = (): MediaQueryList | null =>
  typeof window === 'undefined' ? null : window.matchMedia('(prefers-color-scheme: light)')

export function getThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'auto' || stored === 'light' || stored === 'dark') return stored
    const legacy = localStorage.getItem(LEGACY_KEY)
    if (legacy !== null) {
      const wasLight = JSON.parse(legacy) === true
      const migrated: ThemePreference = wasLight ? 'light' : 'auto'
      localStorage.setItem(STORAGE_KEY, migrated)
      localStorage.removeItem(LEGACY_KEY)
      return migrated
    }
  } catch { /* ignore malformed stored value */ }
  return 'auto'
}

export function setThemePreference(pref: ThemePreference): void {
  try { localStorage.setItem(STORAGE_KEY, pref) } catch { /* ignore quota errors */ }
  applyTheme(pref)
}

export function resolveTheme(pref: ThemePreference): 'light' | 'dark' {
  if (pref !== 'auto') return pref
  return lightQuery()?.matches ? 'light' : 'dark'
}

export function applyTheme(pref: ThemePreference): void {
  const resolved = resolveTheme(pref)
  const root = document.documentElement
  if (resolved === 'light') root.dataset.theme = 'light'
  else delete root.dataset.theme
}

export function subscribeSystemTheme(callback: () => void): () => void {
  const mq = lightQuery()
  if (!mq) return () => {}
  const handler = (): void => callback()
  mq.addEventListener('change', handler)
  return () => mq.removeEventListener('change', handler)
}
